import { useEffect, useMemo, useState } from 'react';
import { isResultFromCurrentSession, withAuthSessionMark } from '../lib/authSessionGuard';
import { useQuery } from '@tanstack/react-query';
import { getCommunicationListAPI } from '../api/commBox';
import { Col, getItemWithExpiration, setItemWithExpiration } from '../lib/utils';
import CommunicationTop from '../components/Communication/CommunicationTop';
import ModalDetailCommunication from '../components/ModalDetailCommunication/ModalDetailCommunication';
import CommunicationPaging from '../components/Communication/CommunicationPaging';
import ModalPictureViewer from '../components/Communication/ModalPictureViewer';
import CommunicationListBody from '../components/Communication/CommunicationListBody';
import CommunicationListHeader from '../components/Communication/CommunicationListHeader';
import BusinessInfoFooter from '../components/BusinessInfoFooter';
import { isKioskCommunicationCategory } from '../lib/communicationBox';

export interface SortType {
    value: string;
    label: string;
}

/*
 * 🔴 강사 필터 목록은 **모듈 변수로 두지 않는다** (2026-09-03 대표 제보).
 *
 * 예전에는 여기 `let coachSortOptions` 가 있었고, 조회 `onSuccess` 가 거기에
 * 강사 이름을 채워 넣었다. 모듈 변수는 로그아웃·로그인·라우팅 어디서도
 * 초기화되지 않는다(새로고침만이 지운다). 그래서 계정을 바꿔 로그인하면
 * **직전 센터의 강사 이름들이 필터에 그대로 남아 있었다.**
 *
 * 지금은 조회 결과에서 그때그때 만든다 — 담아 둘 곳이 없으니 샐 곳도 없다.
 * 아래 두 개는 서버와 무관한 고정 목록이라 모듈에 남겨 둔다.
 */
let contactSortOptions: any = [
    { value: 'ENTIRE', label: '문의 유형 전체' },
    { value: 'PTFEEDBACK', label: 'PT 피드백' },
    { value: 'FACILITY', label: '시설 문의' },
    { value: 'COUNSEL', label: '상담 문의' },
    { value: 'KIOSK', label: '키오스크' },
    { value: 'ETC', label: '기타' },
];

let answerSortOptions: any = [
    { value: 'ENTIRE', label: '답변 진행 전체' },
    { value: 'PENDING', label: '답변대기' },
    { value: 'DONE', label: '답변완료' },
];

const CommunicationPage = ({ createTwoButtonAlert, createAlert, authObject, setAuthObject }: any) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));

    const [coachSortType, setCoachSortType] = useState<SortType>({ value: 'ENTIRE', label: '강사 전체' });
    const [contactSortType, setContactSortType] = useState<SortType>({ value: 'ENTIRE', label: '문의 유형 전체' });
    const [answerSortType, setAnswerSortType] = useState<SortType>({ value: 'ENTIRE', label: '답변 진행 전체' });

    const [filterList, setFilterList] = useState<any>(null);
    const [entireList, setEntireList] = useState<any>(null);
    const [totalNumberCommunication, setTotalNumberCommunication] = useState<number>(0);

    const [isOpenCoach, setIsOpenCoach] = useState<boolean>(false);
    const [isOpenContact, setIsOpenContact] = useState<boolean>(false);
    const [isOpenAnswer, setIsOpenAnswer] = useState<boolean>(false);

    // 페이징
    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filterList && filterList.slice(startIndex, endIndex);
    const totalPages = filterList && Math.ceil(filterList.length / itemsPerPage);

    // 자세히 보기 모달
    const [isDetailModal, setIsDetailModal] = useState<boolean>(false);
    const [communicationObject, setCommunicationObject] = useState<any>(null);

    // 사진뷰어 모달
    const [isPictureModal, setIsPictureModal] = useState<boolean>(false);
    const [pictureList, setPictureList] = useState<any>(null);

    // 외부클릭시 드롭다운닫기
    const onClickOutSide = () => {
        if (isOpenCoach) {
            setIsOpenCoach(false);
        }
        if (isOpenContact) {
            setIsOpenContact(false);
        }
        if (isOpenAnswer) {
            setIsOpenAnswer(false);
        }
    };

    // 다음 페이지
    const onClickNextPage = () => {
        setCurrentPage(currentPage + 1);
        if (currentPage % 5 === 0) {
            setCurrentPage(currentPage + 1);
        }
    };

    // 이전 페이지
    const onClickPrevPage = () => {
        setCurrentPage(currentPage - 1);
    };

    // 맨 처음 페이지로
    const onClickStartPage = () => {
        setCurrentPage(1);
    };
    // 맨 마지막 페이지로
    const onClickLastPage = () => {
        setCurrentPage(totalPages);
    };

    // 자세히보기 버튼 클릭이벤트
    const onClickDetailButton = (item: any) => {
        setCommunicationObject(item);
        setIsDetailModal(true);
    };

    // 필터 초기화 버튼 클릭이벤트
    const onClickFilterReset = () => {
        setCoachSortType({ value: 'ENTIRE', label: '강사 전체' });
        setContactSortType({ value: 'ENTIRE', label: '문의 유형 전체' });
        setAnswerSortType({ value: 'ENTIRE', label: '답변 진행 전체' });
        setCurrentPage(1);
    };

    // 새로고침 버튼 클릭이벤트
    const onClickRefresh = () => {
        getCommunicationList.refetch();
    };

    const onClickPicture = (item: any) => {
        setPictureList(item);
        setIsPictureModal(true);
    };

    const onClickRefetch = () => {
        getCommunicationList.refetch();
    };

    // 회원 소통 상자 리스트 조회 API : (GET)
    const getCommunicationList = useQuery(
        ['getCommunicationListAPI', authorization],
        // 조회를 시작한 세션 표식을 결과에 붙인다 — 늦은 응답이 다른 계정 저장소를 덮지 않게.
        async () => await withAuthSessionMark(() => getCommunicationListAPI(authorization, authObject?.adminId), {
            authObject,
            authorization,
        }),
        {
            onSuccess: (res) => {
                if (!isResultFromCurrentSession(res)) return;
                if (res.status === 200) {
                    const communicationBox = res.data.communicationBox;
                    const coachList = res.data.coachList;
                    const isNewCommunicationBox = res.data.isNewCommunicationBox;

                    console.log('실행');

                    setEntireList(communicationBox);
                    communicationBox && setTotalNumberCommunication(communicationBox.length);

                    /* 강사 필터 목록은 조회 결과에서 파생시킨다 (아래 useMemo) */

                    const authObjectData = {
                        // 목록에 없는 기존 필드(정산 방식 settlementType·부가세·급여 비밀번호 설정 등)를 버리지 않는다 — 아래 값만 덮어쓴다.
                        ...authObject,
                        adminId: authObject?.adminId,
                        id: authObject?.id,
                        name: authObject?.name,
                        email: authObject?.email,
                        gender: authObject?.gender,
                        phoneNumber: authObject?.phoneNumber,
                        profileImageUrl: authObject?.profileImageUrl,
                        /** 센터 */
                        centerAddress: authObject?.centerAddress,
                        centerCategory: authObject?.centerCategory,
                        centerCode: authObject?.centerCode,
                        centerName: authObject?.centerName,
                        centerId: authObject?.centerId,
                        isNewCommunicationBox: isNewCommunicationBox,

                        /** 구독플랜관련 */
                        coachCount: authObject?.coachCount, // 소속 트레이너수
                        memberCount: authObject?.memberCount, // 활성 수강생수
                        activationDate: authObject?.activationDate, // 플랜 활성일
                        expirationDate: authObject?.expirationDate, // 플랜 만료일
                        expirationDelayDate: authObject?.expirationDelayDate, // 플랜 만료후 유예기간
                        finalPaymentDate: authObject?.finalPaymentDate, // 마지막 결제일
                        finalPaymentTryDate: authObject?.finalPaymentTryDate, // 마지막 결제 시도일

                        cardIssuingBank: authObject?.cardIssuingBank, // 결제카드 은행
                        cardNumber: authObject?.cardNumber, // 결제카드 번호
                        planAmount: authObject?.planAmount, // 결제 금액
                        usableStatus: authObject?.usableStatus, // 구독상태
                        paymentDueDate: authObject?.paymentDueDate,

                        /** 구독플랜 인원 정보 */
                        planLimitCount: authObject?.planLimitCount, // 플랜 제한 이용 인원
                        prevPlanLimitCount: authObject?.prevPlanLimitCount, // 구독 인원 변경시 변경전 플랜카운트
                        changedPlanLimitCount: authObject?.changedPlanLimitCount, // 구독 인원 변경시 변경 플랜카운트

                        confirmUsableStatus: authObject?.confirmUsableStatus, // 구독상태의 따른 팝업 클릭시 12시간이내에 다시안뜨게 하는 변수
                        testAccount: authObject?.testAccount, // 데모버전 계정인지아닌지
                    };
                    setAuthObject(authObjectData);
                    setItemWithExpiration('authObject', authObjectData, 12);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            staleTime: 0,
        }
    );

    /**
     * 강사 필터 목록 — **조회 결과에서 그때그때 만든다.**
     *
     * `onSuccess` 에서 어딘가에 담아 두지 않는 이유는 두 가지다.
     *   ① 담아 둔 곳이 계정 전환에도 안 지워지면 남의 센터 강사가 남는다
     *      (모듈 변수로 두었다가 실제로 그랬다 — 파일 위 주석 참고).
     *   ② `onSuccess` 는 **다시 받아왔을 때만** 불린다. 캐시가 살아 있는 채로
     *      화면에 돌아오면 안 불려서, 담아 두는 방식은 목록이 비는 순간이 생긴다.
     */
    const coachSortOptions = useMemo(() => {
        const coachList = getCommunicationList.data?.data?.coachList ?? [];
        return [
            { value: 'ENTIRE', label: '강사 전체' },
            ...coachList.map((coach: any) => ({ value: coach.coachId, label: coach.coachName })),
        ];
    }, [getCommunicationList.data]);

    useEffect(() => {
        if (entireList) {
            if (
                coachSortType.value === 'ENTIRE' &&
                contactSortType.value === 'ENTIRE' &&
                answerSortType.value === 'ENTIRE'
            ) {
                setFilterList(entireList);
                return;
            } else {
                const filteredList = entireList.filter((item: any) => {
                    const coachFilter =
                        coachSortType.value === 'ENTIRE' ? entireList : item.coachId === coachSortType.value;
                    const contactFilter =
                        contactSortType.value === 'ENTIRE'
                            ? entireList
                            : contactSortType.value === 'KIOSK'
                            ? isKioskCommunicationCategory(item.category)
                            : item.category === contactSortType.value;
                    const answerFilter =
                        answerSortType.value === 'ENTIRE' ? entireList : item.status === answerSortType.value;

                    return coachFilter && contactFilter && answerFilter;
                });

                let pendingList = filteredList.filter((item: any) => item.status === 'PENDING');

                let doneList = filteredList.filter((item: any) => item.status === 'DONE');

                pendingList.sort((a: any, b: any) => a.updatedTime - b.updatedTime);
                doneList.sort((a: any, b: any) => a.updatedTime - b.updatedTime);

                let finalFilterList = [].concat(pendingList, doneList);
                setFilterList(finalFilterList);
            }
        }
    }, [coachSortType, contactSortType, answerSortType, entireList]);

    useEffect(() => {
        if (contactSortType.value && answerSortType.value && coachSortType.value) {
            setCurrentPage(1);
        }
    }, [contactSortType.value, answerSortType.value, coachSortType.value]);
    return (
        <Col onClick={onClickOutSide} style={{ width: '100%', alignItems: 'center', marginTop: 64, marginBottom: 80 }}>
            <CommunicationTop
                // 코치 필터
                coachSortOptions={coachSortOptions}
                coachSortType={coachSortType}
                setCoachSortType={setCoachSortType}
                isOpenCoach={isOpenCoach}
                setIsOpenCoach={setIsOpenCoach}
                // 문의 유형 필터
                contactSortOptions={contactSortOptions}
                contactSortType={contactSortType}
                setContactSortType={setContactSortType}
                isOpenContact={isOpenContact}
                setIsOpenContact={setIsOpenContact}
                // 답변 진행 상태 필터
                answerSortOptions={answerSortOptions}
                answerSortType={answerSortType}
                setAnswerSortType={setAnswerSortType}
                isOpenAnswer={isOpenAnswer}
                setIsOpenAnswer={setIsOpenAnswer}
                onClickFilterReset={onClickFilterReset}
                onClickRefresh={onClickRefresh}
                totalNumberCommunication={totalNumberCommunication}
            />

            <CommunicationPaging
                filterList={filterList}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onClickPrevPage={onClickPrevPage}
                onClickNextPage={onClickNextPage}
                onClickStartPage={onClickStartPage}
                onClickLastPage={onClickLastPage}
                totalNumberCommunication={totalNumberCommunication}
            />

            <Col style={{ width: 1192, marginTop: 15 }}>
                <CommunicationListHeader />
                <CommunicationListBody
                    currentItems={currentItems}
                    onClickDetailButton={onClickDetailButton}
                    communicationObject={communicationObject}
                />
            </Col>

            {isDetailModal && (
                <ModalDetailCommunication
                    setIsDetailModal={setIsDetailModal}
                    communicationObject={communicationObject}
                    setCommunicationObject={setCommunicationObject}
                    onClickRefetch={onClickRefetch}
                    createTwoButtonAlert={createTwoButtonAlert}
                    createAlert={createAlert}
                    onClickPicture={onClickPicture}
                />
            )}

            {isPictureModal && <ModalPictureViewer setIsPictureModal={setIsPictureModal} pictureList={pictureList} />}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default CommunicationPage;
